import React, { useEffect, useState } from "react";
import { api, formatDateTime, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageSquareWarning, Plus, Trash2, Trophy, Clock, AlertTriangle, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PRIO_STYLES = {
  urgent: { bg: "bg-red-50 border-red-300", text: "text-red-800", label: "URGENT", icon: AlertTriangle },
  warning: { bg: "bg-amber-50 border-amber-300", text: "text-amber-800", label: "REMINDER", icon: Clock },
  info: { bg: "bg-blue-50 border-blue-300", text: "text-blue-800", label: "INFO", icon: Info },
};

export default function Announcements() {
  const { isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", priority: "info" });

  const load = () => api.get("/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Isi judul dan isi pengumuman");
    try {
      await api.post("/announcements", form);
      toast.success("Pengumuman diterbitkan");
      setOpen(false); setForm({ title: "", body: "", priority: "info" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus pengumuman ini?")) return;
    try { await api.delete(`/announcements/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <PageHeader title="Pengumuman" subtitle="Informasi penting untuk seluruh tim & mitra" testId="page-announcements"
        actions={isOwner && (
          <Button onClick={() => setOpen(true)} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]" data-testid="btn-add-announcement">
            <Plus className="w-4 h-4 mr-2"/> Buat Pengumuman
          </Button>
        )}
      />

      <div className="space-y-4">
        {items.length === 0 && (
          <Card className="p-8 text-center text-slate-500">Belum ada pengumuman.</Card>
        )}
        {items.map((a) => {
          const s = PRIO_STYLES[a.priority] || PRIO_STYLES.info;
          const Icon = s.icon;
          return (
            <Card key={a.id} className={`p-5 border-2 ${s.bg} rounded-md`} data-testid={`announcement-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${s.text}`}/>
                    <Badge variant="outline" className={`uppercase text-[10px] ${s.text}`}>{s.label}</Badge>
                    {a.auto && <Badge variant="outline" className="text-[10px] bg-white">otomatis</Badge>}
                    <span className="text-xs text-slate-500 ml-auto">{formatDateTime(a.created_at)}</span>
                  </div>
                  <h3 className={`font-bold text-base mb-2 ${s.text}`} style={{fontFamily:'Outfit'}}>{a.title}</h3>
                  <div className={`text-sm whitespace-pre-line ${s.text}`}>{a.body}</div>
                </div>
                {isOwner && !a.auto && (
                  <Button size="icon" variant="ghost" onClick={() => del(a.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Buat Pengumuman Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Prioritas</Label>
              <Select value={form.priority} onValueChange={(v)=>setForm({...form,priority:v})}>
                <SelectTrigger data-testid="select-priority"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info (biru)</SelectItem>
                  <SelectItem value="warning">Reminder (kuning)</SelectItem>
                  <SelectItem value="urgent">Urgent (merah)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Judul</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} data-testid="input-title"/></div>
            <div><Label>Isi</Label><Textarea rows={5} value={form.body} onChange={(e)=>setForm({...form,body:e.target.value})} data-testid="input-body"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="btn-save-announcement" className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">Terbitkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
